export interface CommissionRateIntervalInput {
  fromMonth: number | null;
  toMonth: number | null;
  rate: number | null;
}

export interface CommissionRowValidationInput {
  productLineLabel: string;
  currencyCode: string;
  currencyLabel?: string;
  rowKey?: number;
  calculationTypeId: number | null;
  expenseRate: number | null;
  intervals: CommissionRateIntervalInput[];
}

export type CommissionValidationField = 'tipo' | 'gasto' | 'intervalos';

export interface CommissionValidationError {
  message: string;
  rowKey?: number;
  field?: CommissionValidationField;
}

const MAX_RATE = 999.99;

export const formatCommissionRowLabel = (row: Pick<CommissionRowValidationInput, 'productLineLabel' | 'currencyCode' | 'currencyLabel'>): string =>
  `${row.productLineLabel} · ${row.currencyLabel ?? row.currencyCode}`;

export const isConfiguredRow = (
  calculationTypeId: number | null,
  expenseRate: number | null,
  intervals: CommissionRateIntervalInput[],
): boolean =>
  calculationTypeId != null && expenseRate != null && intervals.length > 0;

export const isEmptyRow = (
  calculationTypeId: number | null,
  expenseRate: number | null,
  intervals: CommissionRateIntervalInput[],
): boolean =>
  calculationTypeId == null && expenseRate == null && intervals.length === 0;

export const isPartialRow = (
  calculationTypeId: number | null,
  expenseRate: number | null,
  intervals: CommissionRateIntervalInput[],
): boolean => !isConfiguredRow(calculationTypeId, expenseRate, intervals) && !isEmptyRow(calculationTypeId, expenseRate, intervals);

export const getPartialRowField = (
  calculationTypeId: number | null,
  expenseRate: number | null,
  intervals: CommissionRateIntervalInput[],
): CommissionValidationField | undefined => {
  if (calculationTypeId == null && (expenseRate != null || intervals.length > 0)) {
    return 'tipo';
  }
  if (expenseRate == null && (calculationTypeId != null || intervals.length > 0)) {
    return 'gasto';
  }
  if (intervals.length === 0 && (calculationTypeId != null || expenseRate != null)) {
    return 'intervalos';
  }
  return undefined;
};

export const findNonConsecutiveGap = (
  intervals: CommissionRateIntervalInput[],
): { expected: number } | null => {
  if (intervals.length < 2) return null;
  const sorted = [...intervals].sort((a, b) => (a.fromMonth ?? 0) - (b.fromMonth ?? 0));
  for (let index = 0; index < sorted.length - 1; index += 1) {
    const current = sorted[index];
    if (current.toMonth == null) continue;
    const next = sorted[index + 1];
    const expected = current.toMonth + 1;
    if ((next.fromMonth ?? 0) !== expected) {
      return { expected };
    }
  }
  return null;
};

export const hasOverlappingIntervals = (intervals: CommissionRateIntervalInput[]): boolean => {
  const sorted = [...intervals].sort((a, b) => (a.fromMonth ?? 0) - (b.fromMonth ?? 0));
  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index];
    const currentEnd = current.toMonth ?? Number.MAX_SAFE_INTEGER;
    for (let nextIndex = index + 1; nextIndex < sorted.length; nextIndex += 1) {
      const next = sorted[nextIndex];
      if ((next.fromMonth ?? 0) <= currentEnd) {
        return true;
      }
    }
  }
  return false;
};

export const validateCommissionRowDetailed = (
  row: CommissionRowValidationInput,
): CommissionValidationError | null => {
  const rowLabel = formatCommissionRowLabel(row);
  const rowKey = row.rowKey;

  if (isPartialRow(row.calculationTypeId, row.expenseRate, row.intervals)) {
    return {
      message: `Fila incompleta en ${rowLabel}. Completá tipo, gasto e intervalos o dejá la fila vacía.`,
      rowKey,
      field: getPartialRowField(row.calculationTypeId, row.expenseRate, row.intervals),
    };
  }

  if (!isConfiguredRow(row.calculationTypeId, row.expenseRate, row.intervals)) {
    return null;
  }

  if (row.calculationTypeId == null) {
    return {
      message: `Tipo requerido en ${rowLabel}`,
      rowKey,
      field: 'tipo',
    };
  }

  if (row.expenseRate == null) {
    return {
      message: `Gasto requerido en ${rowLabel}`,
      rowKey,
      field: 'gasto',
    };
  }

  if (row.expenseRate < 0 || row.expenseRate > MAX_RATE) {
    return {
      message: `Gasto inválido en ${rowLabel}`,
      rowKey,
      field: 'gasto',
    };
  }

  if (row.intervals.length === 0) {
    return {
      message: `Al menos un intervalo requerido en ${rowLabel}`,
      rowKey,
      field: 'intervalos',
    };
  }

  for (const interval of row.intervals) {
    if (interval.fromMonth == null || interval.fromMonth < 0) {
      return {
        message: `Desde mes inválido en ${rowLabel}`,
        rowKey,
        field: 'intervalos',
      };
    }
    if (interval.toMonth != null && interval.toMonth < interval.fromMonth) {
      return {
        message: `Hasta mes inválido en ${rowLabel}`,
        rowKey,
        field: 'intervalos',
      };
    }
    if (interval.rate == null || interval.rate < 0 || interval.rate > MAX_RATE) {
      return {
        message: `Tasa inválida en ${rowLabel}`,
        rowKey,
        field: 'intervalos',
      };
    }
  }

  const infiniteIntervals = row.intervals.filter((interval) => interval.toMonth == null);
  if (infiniteIntervals.length > 1) {
    return {
      message: `Solo un intervalo abierto permitido en ${rowLabel}`,
      rowKey,
      field: 'intervalos',
    };
  }

  if (infiniteIntervals.length === 1) {
    const maxFromMonth = Math.max(...row.intervals.map((interval) => interval.fromMonth ?? 0));
    if (infiniteIntervals[0].fromMonth !== maxFromMonth) {
      return {
        message: `El intervalo abierto debe ser el último en ${rowLabel}`,
        rowKey,
        field: 'intervalos',
      };
    }
  }

  const gap = findNonConsecutiveGap(row.intervals);
  if (gap) {
    return {
      message: `Intervalos no consecutivos en ${rowLabel}. El siguiente debe empezar en mes ${gap.expected}.`,
      rowKey,
      field: 'intervalos',
    };
  }

  if (hasOverlappingIntervals(row.intervals)) {
    return {
      message: `Intervalos solapados en ${rowLabel}`,
      rowKey,
      field: 'intervalos',
    };
  }

  return null;
};

export const validateCommissionRow = (row: CommissionRowValidationInput): string | null => {
  const error = validateCommissionRowDetailed(row);
  return error?.message ?? null;
};

export const validateCommissionRowsDetailed = (
  rows: CommissionRowValidationInput[],
): CommissionValidationError | null => {
  for (const row of rows) {
    const error = validateCommissionRowDetailed(row);
    if (error) {
      return error;
    }
  }
  return null;
};

export const validateCommissionRows = (rows: CommissionRowValidationInput[]): string | null => {
  const error = validateCommissionRowsDetailed(rows);
  return error?.message ?? null;
};
